/**
 * Blocks a writer drops into the body: a CTA, a supervisor, a link.
 *
 * Dragging works with no drop handler at all. A textarea accepts dropped
 * `text/plain` natively and inserts it where the pointer is, which is both the
 * behaviour people expect and one fewer place to get the caret position wrong.
 * Every tile also has a button, because dragging is not available to somebody
 * using a keyboard and this is the only way to insert.
 *
 * The blocks are built from approved rows only - the same rows the generator may
 * state - so inserting one cannot introduce a claim the article is not allowed
 * to make.
 */
import { useState, type CSSProperties } from 'react';
import type { CatalogItem } from '../api.ts';
import { ctaBlock, linkBlock, supervisorBlock } from './insert-markdown.ts';
import { ColorPicker } from './ColorPicker.tsx';
import { THEME_PRESETS, blockStyle } from '../theme.ts';
import { currentBrand } from '../theme-apply.ts';

/** `--a: b; --c: d` as a React style object, for the preview. */
const styleOf = (css: string): CSSProperties =>
  Object.fromEntries(
    css
      .split(';')
      .map((pair) => pair.split(':').map((part) => part.trim()))
      .filter(([key]) => key),
  ) as CSSProperties;
import { MARKDOWN_DRAG_MIME } from '../rich-text.ts';

/** Catalog rows carry open-ended columns; only strings are safe to render. */
const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

export function InsertBlocks({
  ctaTemplates,
  supervisors,
  onInsert,
}: {
  ctaTemplates: CatalogItem[];
  supervisors: CatalogItem[];
  /** Insert at the caret. The editor owns the text, so it does the splicing. */
  onInsert: (markdown: string) => void;
}) {
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkKind, setLinkKind] = useState<'text' | 'button'>('text');
  /** null = the clinic's theme, which is also what a later theme change follows. */
  const [linkColor, setLinkColor] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const tile = (key: string, label: string, hint: string, markdown: string) => (
    <div
      key={key}
      className="tile-drag"
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData(MARKDOWN_DRAG_MIME, markdown);
        event.dataTransfer.setData('text/plain', markdown);
        event.dataTransfer.effectAllowed = 'copy';
      }}
    >
      <div>
        <div className="tile-drag__name">{label}</div>
        <div className="muted small">{hint}</div>
      </div>
      <button className="btn btn--ghost btn--sm" type="button" onClick={() => onInsert(markdown)}>
        挿入
      </button>
    </div>
  );

  const httpsOnly = linkUrl === '' || /^https:\/\//.test(linkUrl);

  return (
    <>
      <p className="muted small" style={{ marginTop: 0 }}>
        タイルを本文にドラッグするか、「挿入」でカーソル位置に入れられます。承認済みの内容だけが
        入ります。
      </p>

      {ctaTemplates.length === 0 && supervisors.length === 0 ? (
        <p className="muted small">
          承認済みのCTAと監修医がありません。CTAテンプレート・監修医の画面で登録・承認してください。
        </p>
      ) : (
        <div className="tile-drags">
          {ctaTemplates.map((cta) =>
            tile(
              `cta-${cta.id}`,
              asText(cta.name) || 'CTA',
              asText(cta.heading) || '見出し未設定',
              ctaBlock(cta),
            ),
          )}
          {supervisors.map((supervisor) =>
            tile(
              `sup-${supervisor.id}`,
              asText(supervisor.name) || '監修医',
              asText(supervisor.title) || '肩書未設定',
              supervisorBlock(supervisor),
            ),
          )}
        </div>
      )}

      <p className="form__section">リンクを挿入</p>
      <div className="editpanel__body">
        <div className="field field--full">
          <span className="field__label">表示</span>
          <div className="chips" role="group" aria-label="リンクの表示">
            <button type="button" className="chip" aria-pressed={linkKind === 'text'} onClick={() => setLinkKind('text')}>
              テキストリンク
            </button>
            <button type="button" className="chip" aria-pressed={linkKind === 'button'} onClick={() => setLinkKind('button')}>
              ボタン
            </button>
          </div>
        </div>
        <div className="field">
          <label htmlFor="ins-label">{linkKind === 'button' ? 'ボタンの文字' : 'リンク文字'}</label>
          <input
            id="ins-label"
            maxLength={120}
            placeholder="例：ダーマペンの料金を見る"
            value={linkLabel}
            onChange={(e) => setLinkLabel(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="ins-url">URL</label>
          <input
            id="ins-url"
            type="url"
            maxLength={2000}
            placeholder="https://"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
          {/* https only, like every other link this application stores. */}
          {!httpsOnly && <span className="field__hint">https:// で始まるURLを入力してください。</span>}
        </div>
        {/*
          The same swatches as the theme screen and the editor's block strip, so
          a colour chosen here is one somebody has already seen elsewhere.
        */}
        <div className="field field--full">
          <span className="field__label">色</span>
          <div className="linkcolor" role="group" aria-label="リンクの色">
            <button type="button" className="chip" aria-pressed={linkColor === null} onClick={() => setLinkColor(null)}>
              テーマの色
            </button>
            {THEME_PRESETS.map((preset) => (
              <button
                key={preset.hex}
                type="button"
                className="themepick__swatch docbar__swatch"
                style={{ background: preset.hex }}
                title={preset.label}
                aria-label={preset.label}
                aria-pressed={linkColor === preset.hex}
                onClick={() => setLinkColor(preset.hex)}
              />
            ))}
            <button type="button" className="chip" aria-pressed={pickerOpen} onClick={() => setPickerOpen((open) => !open)}>
              その他の色
            </button>
          </div>
          {pickerOpen && (
            <ColorPicker idPrefix="ins-link-color" value={linkColor ?? currentBrand()} onChange={setLinkColor} />
          )}
        </div>
        <div className="field field--full">
          <span className="field__label">プレビュー</span>
          <div className="linkpreview" aria-hidden>
            {linkKind === 'button' ? (
              <span className="ctabox__button" style={linkColor ? styleOf(blockStyle(linkColor)) : undefined}>
                {linkLabel.trim() || '詳しくはこちら'}
              </span>
            ) : (
              <span className="linkpreview__text" style={linkColor ? { color: linkColor } : undefined}>
                {linkLabel.trim() || linkUrl || 'リンク文字'}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="editpanel__actions">
        <button
          className="btn btn--ghost btn--sm"
          type="button"
          disabled={linkUrl === '' || !httpsOnly}
          onClick={() => {
            onInsert(linkBlock({ kind: linkKind, label: linkLabel, url: linkUrl, color: linkColor }));
            setLinkLabel('');
            setLinkUrl('');
          }}
        >
          リンクを挿入
        </button>
      </div>
    </>
  );
}
